<template>
  <div class="wrapper">
    <h2 class="component-title">
      {{ $t('choosePaymentMethod') }}
    </h2>
    <p :class="['about-next-step', isZkm && 'about-next-step-zkm']">
      {{ $t('finishPaymentInNextStep') }}
    </p>
    <div class="payment-methods">
      <p
        v-if="paymentMethodsLoading"
        role="status"
      >
        {{ $t('paymentMethodsLoading') }}
      </p>
      <p
        v-else-if="!providerMethods.length && !isKulturnaIskaznicaPaymentEnabled && !isGiftCardPaymentEnabled"
        role="alert"
      >
        {{ $t(paymentMethodsError ? 'paymentMethodsError' : 'paymentMethodsUnavailable') }}
      </p>
      <button
        v-for="option in providerMethods"
        :key="option.id"
        type="button"
        :class="[
          'payment-method',
          isZkm && 'payment-method-zkm',
          selected === option.id && 'selected-method',
          isRevolutMethod(option) && 'revolut-method',
        ]"
        :aria-pressed="selected === option.id"
        @click="selectPaymentMethod(option.id)"
      >
        <div class="payment-description">
          <div class="check-box">
            <span v-if="selected === option.id" class="material-icons">{{ determineButtonType(option.id) }}</span>
            <img v-else-if="isRevolutMethod(option)" src="/assets/revolut-payment/radio.svg" alt="">
            <span v-else class="material-icons">{{ determineButtonType(option.id) }}</span>
          </div>
          <div class="payment-text">
            <h3 class="payment-type">
              {{ isRevolutMethod(option) ? $t('revolutPaymentTitle') : paymentTitle(option) }}
            </h3>
            <p class="payment-about">
              {{ isRevolutMethod(option) ? $t('revolutPaymentDescription') : paymentDescription(option) }}
            </p>
          </div>
        </div>
        <div v-if="isRevolutMethod(option)" class="revolut-payment-badges" aria-hidden="true">
          <img src="/assets/revolut-payment/visa.svg" alt="">
          <span class="revolut-payment-badge">
            <img src="/assets/revolut-payment/mastercard.svg" alt="">
          </span>
          <img src="/assets/revolut-payment/revolut.svg" alt="">
          <img src="/assets/revolut-payment/apple-pay.svg" alt="">
          <span class="revolut-payment-badge google-pay-badge">
            <img src="/assets/revolut-payment/google-g.svg" alt="">
            <img src="/assets/revolut-payment/google-pay-wordmark.svg" alt="">
          </span>
        </div>
        <div v-else class="payment-logo">
          <img
            v-if="paymentLogo(option)"
            :src="paymentLogo(option)"
            :alt="paymentTitle(option)"
          >
        </div>
      </button>
      <div
        v-if="isKulturnaIskaznicaPaymentEnabled"
        :class="[
          'payment-method',
          isZkm && 'payment-method-zkm',
          selected === 'KulturnaIskaznica' && 'selected-method',
        ]"
        @click="selectPaymentMethod('KulturnaIskaznica')"
      >
        <div class="payment-description">
          <div class="check-box">
            <span class="material-icons">
              {{ determineButtonType('KulturnaIskaznica') }}
            </span>
          </div>
          <div class="payment-text">
            <h3 class="payment-type">
              {{ $t('kiPayment') }}
            </h3>
            <p class="payment-about">
              {{ $t('kiPaymentDescription') }}
            </p>
          </div>
        </div>
        <div class="payment-logo">
          <img
            src="/assets/kulturna-iskaznica-logo.jpg"
            :alt="$t('kiPayment')"
            style="max-height: 40px"
          >
        </div>
      </div>
      <div
        v-if="isGiftCardPaymentEnabled"
        :class="[
          'payment-method',
          isZkm && 'payment-method-zkm',
          isGiftCardSelected && 'selected-method',
        ]"
        @click=";(isGiftCardSelected = !isGiftCardSelected), $emit('paymentMethod', 'GiftCard')"
      >
        <div class="payment-description">
          <div class="check-box">
            <span class="material-icons">
              {{ isGiftCardSelected ? 'check_box' : 'check_box_outline_blank' }}
            </span>
          </div>
          <div class="payment-text">
            <h3 class="payment-type">
              {{ $t('giftCardPayment') }}
            </h3>
            <p class="payment-about">
              {{ $t('giftCardPaymentDescription') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import paymentMethodsMixin from '@/payment-methods-mixin'
import { getKiConfig } from '../../../http'

export default {
  name: 'payment-method',
  mixins: [paymentMethodsMixin],
  props: ['event', 'tickets'],
  data() {
    return {
      selected: null,
      isGiftCardSelected: false,
      kulturnaIskaznicaPaymentOrganizations: [],
    }
  },
  computed: {
    paymentEligibility() { return { keksPay: this.event.id === '905', payCek: this.hidePaycek } },
    isZkm() {
      return this.event.organization.id == process.env.VUE_APP_ZKM_ORGANIZATION_ID
    },
    isGiftCardPaymentEnabled() {
      return this.paymentCatalog.additionalMethods.GiftCard === true && !!this.event.eventTemplateId
    },
    isKulturnaIskaznicaPaymentEnabled() {
      const organization = this.event && this.event.organization

      if (this.paymentCatalog.additionalMethods.KulturnaIskaznica !== true || !organization || !this.kulturnaIskaznicaPaymentOrganizations.length) {
        return false
      }

      return [organization.id, organization.slug]
        .filter(Boolean)
        .map(organizationIdentifier => String(organizationIdentifier).toLowerCase())
        .some(organizationIdentifier =>
          this.kulturnaIskaznicaPaymentOrganizations.includes(organizationIdentifier),
        )
    },
    hidePaycek() {
      const ticketIds = this.tickets.map(t => t.ticketId)

      return !ticketIds.some(id => [8591, 8593].includes(Number(id)))
    },
  },
  watch: {
    isKulturnaIskaznicaPaymentEnabled(enabled) {
      if (enabled && !this.selected) this.selectPaymentMethod('KulturnaIskaznica')
    },
  },
  async mounted() {
    try {
      const config = await getKiConfig()
      this.kulturnaIskaznicaPaymentOrganizations = config.allowedOrganizations || []
    } catch (error) {
      console.error('Failed to load Kulturna iskaznica config', error)
      this.kulturnaIskaznicaPaymentOrganizations = []
    }
  },
  methods: {
    selectPaymentMethod(method) {
      if (method && method !== 'KulturnaIskaznica' && !this.providerMethods.some(option => option.id === method)) return
      this.selected = method
      this.$emit('paymentMethod', method)
    },
    isRevolutMethod(option) {
      return option.provider === 'REVOLUT' && option.method === 'CreditCard'
    },
    determineButtonType(option) {
      if (this.isGiftCardPaymentEnabled) {
        return this.selected === option ? 'check_box' : 'check_box_outline_blank'
      }

      return this.selected === option ? 'check_circle' : 'radio_button_unchecked'
    },
  },
}
</script>

<style scoped>
.payment-methods {
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
}

.payment-method {
  border: 1px solid #979797;
  border-radius: 4px;
  padding: 1.125rem;
  width: 100%;
  min-height: 85px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #979797;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.payment-method:focus-visible {
  outline: 2px solid #0277bd;
  outline-offset: 2px;
}

.revolut-method {
  gap: 16px;
  padding: 16px 24px 16px 16px;
  border-color: rgba(151, 151, 151, 0.5);
  align-items: flex-start;
}

.revolut-method .payment-description {
  min-width: 0;
  flex: 1;
  gap: 16px;
}

.revolut-method .check-box {
  width: 24px;
  height: 24px;
  flex: none;
  margin: 0;
}

.revolut-method .check-box img {
  display: block;
}

.revolut-method .payment-text {
  min-width: 0;
  padding: 0 0 2px;
}

.revolut-method .payment-type {
  margin: 0;
  font-family: 'Raleway';
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.6px;
}

.revolut-method .payment-about {
  margin: 8px 0 0;
  font-family: 'Raleway';
  font-size: 16px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.24px;
}

.revolut-payment-badges {
  display: flex;
  flex: none;
  align-self: stretch;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.revolut-payment-badges > img,
.revolut-payment-badge > img {
  display: block;
  flex: none;
}

.revolut-payment-badge {
  display: flex;
  width: 34px;
  height: 24px;
  flex: none;
  align-items: center;
  justify-content: center;
  border: 1px solid #f2f4f7;
  border-radius: 4px;
  background: #fff;
}

.google-pay-badge {
  position: relative;
}

.google-pay-badge img {
  position: absolute;
}

.google-pay-badge img:first-child {
  left: 5px;
  top: 8px;
}

.google-pay-badge img:last-child {
  right: 5px;
  top: 8px;
}

.payment-method-zkm {
  max-width: inherit !important;
}

@media screen and (max-width: 455px) {
  .payment-method {
    flex-direction: column !important;
    align-items: flex-start !important;
  }

  .payment-logo {
    align-self: center;
  }

  .revolut-method {
    gap: 12px;
  }

  .revolut-payment-badges {
    align-self: flex-end;
    flex-wrap: wrap;
  }
}

.check-box {
  margin-right: 1.125rem;
  margin-top: 5px;
}

.payment-description {
  display: flex;
}

.payment-logo {
  margin-right: 1.5rem;
}

.component-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  color: #333333;
  margin-bottom: 1rem;
  margin-top: 1.375rem;
}

.wrapper {
  margin-bottom: 2rem;
}

.selected-method {
  color: #0277bd;
  border: 1px solid #0277bd !important;
}

.payment-text {
  padding-right: 0.25rem;
}

.about-next-step {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #666666;
}

.about-next-step-zkm {
  padding-bottom: 2rem;
}

.payment-type {
  font-weight: 500;
  font-size: 20px;
}

.payment-about {
  margin-top: 0.5rem;
  font-weight: 400;
  font-size: 16px;
}
</style>
